import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../lib/config";
import { Church, Route as RouteIcon, TentTree, Home, User as UserIcon, ArrowLeft, Maximize2 } from "lucide-react";

const STATUS_META = [
  { key: "at_church",       label: "En iglesia",           icon: Church,    color: "from-blue-500 to-blue-700",       ring: "ring-blue-400"   },
  { key: "left_church",     label: "Salió de iglesia",     icon: RouteIcon, color: "from-cyan-500 to-cyan-700",       ring: "ring-cyan-400"   },
  { key: "at_camp",         label: "En campamento",        icon: TentTree,  color: "from-green-500 to-emerald-700",   ring: "ring-green-400"  },
  { key: "left_camp",       label: "Salió del campamento", icon: RouteIcon, color: "from-orange-500 to-orange-700",   ring: "ring-orange-400" },
  { key: "back_at_church",  label: "De regreso",           icon: Home,      color: "from-purple-500 to-purple-700",   ring: "ring-purple-400" },
  { key: "not_checked_in",  label: "Sin registrar",        icon: UserIcon,  color: "from-neutral-500 to-neutral-700", ring: "ring-neutral-400" },
];

export default function LogisticsTV() {
  const nav = useNavigate();
  const [summary, setSummary] = useState(null);
  const [now, setNow] = useState(new Date());
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/logistics/summary");
      setSummary(data);
      setError(null);
    } catch (e) {
      if (e?.response?.status === 401) nav("/admin/login");
      else setError("Sin conexión");
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("admin_token")) { nav("/admin/login"); return; }
    load();
    const dataInt = setInterval(load, 5000);
    const clockInt = setInterval(() => setNow(new Date()), 1000);
    return () => { clearInterval(dataInt); clearInterval(clockInt); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goFullscreen = () => {
    const el = document.documentElement;
    if (!document.fullscreenElement && el.requestFullscreen) el.requestFullscreen();
    else if (document.exitFullscreen) document.exitFullscreen();
  };

  const total = summary?.total || 0;

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden" data-testid="logistics-tv">
      {/* Background */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(250,204,21,0.10),_transparent_65%)] pointer-events-none" />
      <div className="absolute inset-0 grain opacity-30 pointer-events-none" />

      {/* Controls (auto-hide on hover) */}
      <div className="absolute top-4 left-4 right-4 z-20 flex justify-between items-center opacity-30 hover:opacity-100 transition-opacity">
        <button
          onClick={() => nav("/admin/logistica")}
          data-testid="tv-back-btn"
          className="text-white/70 hover:text-yellow-400 text-xs uppercase tracking-widest flex items-center gap-1"
        >
          <ArrowLeft className="w-4 h-4" /> Salir
        </button>
        <button
          onClick={goFullscreen}
          data-testid="tv-fullscreen-btn"
          className="text-white/70 hover:text-yellow-400 text-xs uppercase tracking-widest flex items-center gap-1"
        >
          <Maximize2 className="w-4 h-4" /> Pantalla completa
        </button>
      </div>

      <div className="relative z-10 min-h-screen flex flex-col p-6 sm:p-10">
        {/* Header */}
        <header className="text-center mb-8 sm:mb-10">
          <div className="text-yellow-400/70 text-xs sm:text-sm uppercase tracking-[0.5em] mb-2">
            Operación Rescate · Live
          </div>
          <h1 className="font-heading uppercase text-white tracking-tight text-4xl sm:text-6xl lg:text-7xl leading-none">
            <span className="text-gradient-gold">Logística</span> del campamento
          </h1>
          <div className="mt-3 text-white/50 text-xs sm:text-sm uppercase tracking-widest tabular-nums">
            {now.toLocaleTimeString("es", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
            {" · "}
            {now.toLocaleDateString("es", { weekday: "long", day: "numeric", month: "long" })}
            {error && <span className="text-red-400 ml-2">· {error}</span>}
          </div>
        </header>

        {/* Grid of stats */}
        <div className="flex-1 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4 items-stretch">
          {STATUS_META.map((s, idx) => {
            const Icon = s.icon;
            const count = summary?.status_counts?.[s.key] || 0;
            const pct = total > 0 ? Math.round((count / total) * 100) : 0;
            return (
              <motion.div
                key={s.key}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                data-testid={`tv-pill-${s.key}`}
                className={`relative rounded-3xl bg-gradient-to-br ${s.color} p-6 sm:p-8 flex flex-col justify-between overflow-hidden ring-1 ${s.ring} shadow-2xl`}
              >
                <div className="absolute inset-0 bg-black/20" />
                <div className="relative">
                  <Icon className="w-10 h-10 sm:w-12 sm:h-12 text-white/90 mb-3" />
                  <div className="text-white/80 text-xs sm:text-sm uppercase tracking-[0.25em] font-bold">
                    {s.label}
                  </div>
                </div>
                <div className="relative">
                  <AnimatePresence mode="popLayout">
                    <motion.div
                      key={count}
                      initial={{ scale: 0.7, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 1.3, opacity: 0 }}
                      transition={{ type: "spring", stiffness: 260, damping: 22 }}
                      className="font-heading text-white text-7xl sm:text-8xl lg:text-9xl leading-none tabular-nums"
                    >
                      {count}
                    </motion.div>
                  </AnimatePresence>
                  <div className="text-white/70 text-xs uppercase tracking-widest mt-2">
                    {pct}% del total
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Footer / total */}
        <footer className="mt-6 sm:mt-8 flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-white/10 pt-4 text-center sm:text-left">
          <div className="text-white/50 text-xs sm:text-sm uppercase tracking-[0.3em]">
            Actualiza cada 5 segundos
          </div>
          <div className="flex items-baseline gap-2">
            <span className="font-heading text-yellow-400 uppercase text-3xl sm:text-4xl tracking-wider tabular-nums">{total}</span>
            <span className="text-white/60 text-xs sm:text-sm uppercase tracking-[0.3em]">inscritos totales</span>
          </div>
          <div className="text-white/50 text-xs sm:text-sm uppercase tracking-[0.3em] hidden sm:block">
            Casa de Oración Ven y Ve
          </div>
        </footer>
      </div>
    </div>
  );
}
